<template>
  <b-row class="automated-responses">
    <b-col
      md="4"
      offset-md="1"
      offset-lg="0"
      class="keyword-nav mb-3">
      <ScrollArea
        ref="keywordScrollbar"
        class="keyword-container">
        <ul class="keyword-list">
          <li
            v-for="reply in instantReplies"
            :key="reply.id"
            @click="setCurrentReply(reply)">
            <div
              v-if="reply.static"
              :class="{active: currentReply.id === reply.id, 'has-error': !!validationErrors[reply.id]}"
              class="form-control keyword-tag static">{{ staticReplyData(reply.keyword).title }}</div>
            <b-input
              v-else
              :ref="`keyword-${reply.id}`"
              v-model="reply.keyword"
              :class="{active: currentReply.id === reply.id, 'has-error': !!validationErrors[reply.id]}"
              placeholder="Enter keyword here..."
              class="keyword-tag"
              @keydown.exact.space.native.prevent=""
              @focusout.native="checkForUpdates(true)"/>
            <span
              v-if="validationErrors[reply.id]"
              class="validation-error">{{ validationErrors[reply.id] }}</span>
            <b-dropdown
              v-if="!reply.static"
              class="reply-actions"
              variant="link"
              right
              no-caret
              no-flip>
              <template slot="button-content">
                <icon name="ellipsis-h"/>
              </template>
              <b-dropdown-item
                @click="showDeleteReplyModal(reply.id)">
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </li>
        </ul>
      </ScrollArea>
      <span
        class="add-reply mb-3 mt-3"
        @click="addNewReply">
        <icon
          width="12"
          height="12"
          name="plus"/> Add New Instant Reply
      </span>
      <span
        v-if="validationErrors.incompleteReply"
        class="validation-error">{{ validationErrors.incompleteReply }}</span>
    </b-col>
    <b-col
      lg="4"
      md="5"
      offset-lg="0"
      offset-md="1"
      class="pl-3 mb-3">
      <div class="phone-mockup">
        <div
          ref="phoneScrollbar"
          class="phone-display scroll-area">
          <div class="message-container">
            <div class="outbound">
              <p v-if="currentReply.static">Contact message</p>
              <p v-else>{{ currentReply.keyword ? currentReply.keyword : 'keyword' }}</p>
            </div>
          </div>
          <draggable
            v-model="currentReply.messages"
            handle=".sort-handler"
            draggable=".message-container"
            @end="handleSorting">
            <div
              v-for="message in currentReply.messages"
              :key="message.id"
              class="message-container">
              <div
                :class="{media: (message.type === 'file')}"
                class="inbound">
                <span class="sort-handler">
                  <icon name="sort"/>
                </span>
                <template v-if="message.type === 'text'">
                  <p>
                    <MessageContent
                      :content="message.text"
                      :detect-urls="true"
                      :convert-tag-to-button="true"/>
                  </p>
                </template>
                <template v-if="message.type === 'link'">
                  <p>{{ message.text ? `${message.text} ` : '' }}<a
                    :href="message.url"
                    target="_blank">{{ message.url }}</a></p>
                </template>
                <template v-if="message.type === 'file'">
                  <MediaMessage
                    v-if="message.mime_type"
                    :title="message.text"
                    :mime-type="message.mime_type"
                    :media-url="message.url"
                    :loading="message.loading"
                    :error="message.error"/>
                </template>
                <div class="action-buttons">
                  <span
                    class="action-button edit"
                    @click="updateReplyMessage(message)">
                    <icon name="pencil-alt"/>
                  </span>
                  <span
                    class="action-button delete"
                    @click="showDeleteMessageModal(currentReply.id, message.id)">
                    <icon name="times"/>
                  </span>
                </div>
              </div>
              <div
                v-if="message.requires_reply"
                class="contact-answer">
                <div class="outbound mt-1">
                  <p>Contact message</p>
                </div>
              </div>
            </div>
          </draggable>
          <div class="message-container">
            <div
              v-if="(currentReply.keyword !== 'away')"
              class="inbound add-reply"
              @click="addReplyMessage">
              <p>Add reply here...</p>
            </div>
          </div>
        </div>
      </div>
    </b-col>
    <b-col
      lg="4"
      md="11"
      offset-lg="0"
      offset-md="1"
      class="settings-container">
      <div class="automation-settings-box">
        <div>
          <h4 v-if="currentReply.static">Description</h4>
          <p v-if="currentReply.static">{{ staticReplyData(currentReply.keyword).description }}</p>
        </div>
        <div>
          <h4>Status</h4>
          <p>
            <SwitchCheckbox
              :id="currentReply.id"
              v-model="currentReply.active"
              @input="changeActive"/>
          </p>
        </div>
        <div>
          <h4>
            <span>Unique Interactions</span>
            <icon
              id="unique-interactions"
              v-b-tooltip="getTooltipConfig('Unique monthly interactions with the keyword.')"
              name="info-circle"
              class="info-label"/>
          </h4>
          <p>{{ analytics.unique_interactions }}</p>
        </div>
        <div>
          <h4>
            <span>Total Interactions</span>
            <icon
              id="all-interactions"
              v-b-tooltip="getTooltipConfig('Total monthly interactions with the keyword.')"
              name="info-circle"
              class="info-label"/>
          </h4>
          <p>{{ analytics.all_interactions }}</p>
        </div>
        <!--<template v-if="currentReplyHasLink">
                      <div>
                        <h4>Total Clicked</h4>
                        <p>{{ replyStatistics.total_clicked }}</p>
                      </div>
                      <div>
                        <h4>Clicked By</h4>
                        <p>{{ replyStatistics.clicked_by }}</p>
                      </div>
                    </template>-->
      </div>
    </b-col>
    <ReplyMessageModal
      ref="replyMessageModal"
      :changeable-type="(currentReply.keyword !== 'away')"
      @messageSaved="updateMessage"/>
    <DeleteReplyModal
      ref="deleteReplyModal"
      @deleted="deleteReply"/>
    <DeleteMessageModal
      ref="deleteMessageModal"
      @deleted="deleteMessage"/>
  </b-row>
</template>

<script>
import '@/assets/css/modules/_automation.scss';

import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import randomString from '@/helpers/randomString';
import ReplyMessageModal from './modals/ReplyMessageModal';
import DeleteReplyModal from './modals/DeleteReplyModal';
import DeleteMessageModal from './modals/DeleteMessageModal';
import MediaMessage from '../chat/partials/MediaMessage';
import MessageContent from '../../common/MessageContent';
import SwitchCheckbox from '../../common/SwitchCheckbox';
import ScrollArea from '../../common/ScrollArea';

export default {
  name: 'AutomatedResponses',
  components: {
    draggable,
    ReplyMessageModal,
    DeleteReplyModal,
    DeleteMessageModal,
    SwitchCheckbox,
    ScrollArea,
    MessageContent,
    MediaMessage,
  },
  data() {
    return {
      oldKeyword: '',
      currentReply: {},
      validationErrors: {
        incompleteReply: '',
      },
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters({
      loadingInstantReplies: 'automation/loadingInstantReplies',
      instantReplies: 'automation/instantReplyList',
      instantReplySaved: 'automation/instantReplySaved',
      keywordAnalytics: 'automation/keywordAnalytics',
      apiError: 'automation/apiError',
    }),
    analytics() {
      const timestamp = Object.keys(this.keywordAnalytics.unique_interactions)[0];

      return {
        unique_interactions: this.keywordAnalytics.unique_interactions[timestamp] || 0,
        all_interactions: this.keywordAnalytics.all_interactions[timestamp] || 0,
      };
    },
    // currentReplyHasLink() {
    //   return !!this.$lodash.find(this.currentReply.messages, message => message.type === 'link');
    // },
  },
  watch: {
    loadingInstantReplies(loading) {
      if (!loading) {
        this.setCurrentReply(this.instantReplies[0]);
        // if (this.$route.params.keyword) {
        //   const reply = this.$lodash.find(this.instantReplies, reply => reply.keyword === this.$route.params.keyword);
        //   this.setCurrentReply(reply);
        // } else {
        // }
      }
    },
    instantReplySaved(payload) {
      if (payload) {
        let reply = null;
        if (payload.temp_id) {
          reply = this.$lodash.find(this.instantReplies, reply => reply.id === payload.temp_id);
          if (reply) {
            reply.id = payload.reply_id;
          }
        } else {
          reply = this.$lodash.find(this.instantReplies, reply => reply.id === payload.reply_id);
        }

        if (reply) {
          // updating new uploaded file urls
          if (!this.$lodash.isEmpty(payload.uploaded_files)) {
            this.$lodash.forEach(payload.uploaded_files, (fileUrl, messageId) => {
              const message = this.$lodash.find(reply.messages, message => message.id === messageId);
              if (message) {
                message.url = fileUrl;
                delete message.temp_file;
              }
            });
          }

          // updating new created message ids
          if (!this.$lodash.isEmpty(payload.message_ids)) {
            this.$lodash.forEach(payload.message_ids, (messageId, tempId) => {
              const message = this.$lodash.find(reply.messages, message => message.id === tempId);
              if (message) {
                message.id = messageId;
              }
            });
          }
        }
      }
    },
    apiError(error) {
      const replyId = error.reply_id;
      error = error.error;
      if (error.status === 422) {
        this.$set(this.validationErrors, replyId, error.data.errors.keyword[0]);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.leavingPage);

    this.fetchInstantReplyList(this.$route.params.merchant_id);
  },
  methods: {
    ...mapActions({
      fetchInstantReplyList: 'automation/fetchInstantReplyList',
      fetchKeywordAnalytics: 'automation/fetchKeywordAnalytics',
      createInstantReply: 'automation/createInstantReply',
      updateInstantReply: 'automation/updateInstantReply',
    }),
    staticReplyData(keyword) {
      switch (keyword) {
        case 'default': {
          return {
            title: 'Default Reply',
            description: 'Default replay will automatically be sent to a user that messaged you.',
          };
        }
        case 'away': {
          return {
            title: 'Away Reply',
            description: 'This message will automatically be sent to a user that messaged you if your availability is set on Away.',
          };
        }
        default: {
          return {
            title: 'keyword',
          };
        }
      }
    },
    setCurrentReply(reply) {
      if (!this.$lodash.isEmpty(this.currentReply)) {
        if (this.currentReply.id === reply.id) {
          return;
        }

        if (!this.currentReply.keyword && !this.currentReply.messages.length) {
          this.$lodash.remove(this.instantReplies, reply => reply.id === this.currentReply.id);
        }
      }

      if (!this.isTemp(reply.id)) {
        this.fetchKeywordAnalytics({
          merchant_id: this.$route.params.merchant_id,
          reply_id: reply.id,
          data: {
            time_zone: this.$moment(null, false).tz.guess(),
            date_point: 'month',
            start_date: this.$moment().startOf('month').unix(),
            end_date: this.$moment().endOf('month').unix(),
          },
        });
      }

      this.currentReply = reply;
      this.oldKeyword = this.currentReply.keyword;
    },
    changeActive(state) {
      this.currentReply.active = state;
      this.$nextTick(() => {
        this.checkForUpdates();
      });
    },
    deleteReply(id) {
      this.$lodash.remove(this.instantReplies, reply => reply.id === id);
      this.$nextTick(() => {
        this.setCurrentReply(this.instantReplies[0]);
        this.$refs.keywordScrollbar.scrollToTop();
      });
    },
    deleteMessage(id) {
      this.$lodash.remove(this.currentReply.messages, message => message.id === id);
      // splice used to trigger array change for vue
      this.currentReply.messages.splice(this.currentReply.messages.length);
    },
    checkForUpdates(checkKeywordUpdate = false) {
      this.refreshValidationErrors();
      if (this.currentReply.keyword && this.currentReply.messages.length) {
        if (this.isTemp(this.currentReply.id)) {
          this.createReply();
        } else if (!checkKeywordUpdate || (checkKeywordUpdate && (this.oldKeyword !== this.currentReply.keyword))) {
          this.updateReply();
        }
      }
    },
    createReply() {
      const payload = {
        temp_id: this.currentReply.id,
        merchant_id: this.$route.params.merchant_id,
        data: this.collectFormData(),
      };

      this.createInstantReply(payload);
    },
    updateReply() {
      const payload = {
        merchant_id: this.$route.params.merchant_id,
        reply_id: this.currentReply.id,
        data: this.collectFormData(),
      };

      this.updateInstantReply(payload);
    },
    collectFormData() {
      const messageOrder = this.currentReply.messages.map(message => message.id);

      return {
        keyword: this.currentReply.keyword,
        active: this.currentReply.active,
        messages: this.currentReply.messages,
        message_order: messageOrder,
      };
    },
    updateMessage(message) {
      const existingMessageIndex = this.$lodash.findIndex(this.currentReply.messages, msg => msg.id === message.id);
      if (existingMessageIndex !== -1) {
        this.currentReply.messages[existingMessageIndex] = message;
      } else {
        this.currentReply.messages.push(message);
      }

      this.$nextTick(() => {
        this.checkForUpdates();
      });
    },
    addNewReply() {
      this.validationErrors.incompleteReply = '';
      if (this.hasIncompleteReply()) {
        this.validationErrors.incompleteReply = 'You can create only 1 keyword at a time.';
        return;
      }

      const newReply = {
        id: `temp-${randomString()}`,
        keyword: '',
        messages: [],
        active: true,
        static: false,
      };

      this.instantReplies.push(newReply);
      this.$nextTick(() => {
        this.setCurrentReply(newReply);
        this.$refs[`keyword-${newReply.id}`][0].$el.focus();
        this.$refs.keywordScrollbar.scrollToBottom();
      });
    },
    addReplyMessage() {
      this.$refs.replyMessageModal.setMessage({
        id: `temp-${randomString()}`,
        type: '',
        text: '',
        url: '',
        mime_type: '',
        requires_reply: false,
      });

      this.$refs.replyMessageModal.show();
    },
    updateReplyMessage(message) {
      this.$refs.replyMessageModal.setMessage(message);
      this.$refs.replyMessageModal.show();
    },
    openReplyModal() {
      this.$refs.replyMessageModal.show();
    },
    isTemp(replyId) {
      return replyId.lastIndexOf('temp-', 0) === 0;
    },
    showDefaultReplySettingsModal() {
      this.$refs.defaultReplySettingsModal.show();
    },
    showDeleteReplyModal(replyId) {
      this.$refs.deleteReplyModal.replyToDelete(replyId);
      this.$refs.deleteReplyModal.show();
    },
    showDeleteMessageModal(replyId, messageId) {
      this.$refs.deleteMessageModal.messageToDelete(replyId, messageId);
      this.$refs.deleteMessageModal.show();
    },
    hasIncompleteReply() {
      return !!this.$lodash.find(this.instantReplies, reply => reply.keyword.trim() === '' || !reply.messages.length === '');
    },
    refreshValidationErrors() {
      this.$set(this.validationErrors, this.currentReply.id, '');
      this.validationErrors.incompleteReply = '';
    },
    handleSorting() {
      this.$nextTick(() => {
        this.checkForUpdates();
      });
    },
    leavingPage(e) {
      if (!this.hasIncompleteReply()) {
        return false;
      }
      const confirmationMessage = 'Are you sure? You have incomplete changes here which may not be saved!';

      (e || window.event).returnValue = confirmationMessage;
      return confirmationMessage;
    },
    getTooltipConfig(text) {
      return {
        title: text,
        placement: 'right',
        animation: false,
        offset: '0, 5px',
      };
    },
  },
};
</script>
